import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Typography, Grid } from "@mui/material";
import { useAuthRouting, useSignedInState, useSignedInUserState } from "../hooks/useAuthState";
import { MessageType } from "../models/message";
import { StaffInvitationMessage } from "./staffInvitationMessage";
import { RefundMessage } from "./refundMessage";
import LoginWarning from "../shared/warning/loginWarning";
import { LottieIndicator } from "../shared/lottieIndicator";
import { messageService } from "../services/firebase/messageService";
import { viewHightMinusHeader } from "../header";

interface MessagesPageProps {
  dashboardMessagesLoading?: boolean;
  dashboardMessages?: MessageType[];
  dashboardSetMessages?: React.Dispatch<React.SetStateAction<MessageType[]>>;
}

const MessagesPage: React.FC<MessagesPageProps> = ({ dashboardMessagesLoading, dashboardMessages, dashboardSetMessages }) => {
  const [error, setError] = useState<string>("");
  const [loadingAnimationData, setLoadingAnimationData] = useState<any>(null);
  const [nothingHereAnimationData, setNothingHereAnimationData] = useState<any>(null);
  const [pageMessages, pageSetMessages] = useState<MessageType[]>([])
  const [messages, setMessages] = (dashboardMessages != undefined && dashboardSetMessages != undefined) ?
    [dashboardMessages, dashboardSetMessages] :
    [pageMessages, pageSetMessages]
  const [pageMessagesLoading, pageSetMessagesLoading] = useState<boolean>(false)
  const [messagesLoading, setMessagesLoading] = (dashboardMessagesLoading != undefined) ?
    [dashboardMessagesLoading, () => {/* Ignore */ }] :
    [pageMessagesLoading, pageSetMessagesLoading]
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const requestVersion = useRef(0); // this ref tracks the “version” of the request

  const { user, profile } = useSignedInUserState();
  useAuthRouting();

  const nothingHereAnimationPath = "/animation/nothing_here_animation.json";
  const loadingAnimationPath = "/animation/loading_location.json";


  useEffect(() => {
    const fetchMessages = () => {

      // if user isn’t signed in or we have no uid, bail out
      if (!profile) {
        setError("You must be signed in to view messages.");
        return;
      }

      const currentRequest = ++requestVersion.current;
      setMessagesLoading(true);
      setError("");

      messageService
        .fetchMessagesByUser(profile)
        .then((fetched) => {
          // ignore this result if a newer request has already started
          if (currentRequest !== requestVersion.current) return;
          setMessages(fetched);
        })
        .catch((_) => {
          if (currentRequest !== requestVersion.current) return;
          setError("Failed to fetch messages. Please try again later.");
        })
        .finally(() => {
          if (currentRequest !== requestVersion.current) return;
          setMessagesLoading(false);
          setHasLoaded(true);
        });

      // no cleanup needed beyond bumping requestVersion if effect re-runs
    }
    if (dashboardMessagesLoading == undefined) {
      fetchMessages()
    }

  }, [profile]);

  useEffect(() => {
    fetch(loadingAnimationPath)
      .then((res) => res.json())
      .then(setLoadingAnimationData)
      .catch((error) => console.error("Error loading loading animation:", error));
  }, []);

  useEffect(() => {
    fetch(nothingHereAnimationPath)
      .then((res) => res.json())
      .then(setNothingHereAnimationData)
      .catch((error) => console.error("Error loading nothing-here animation:", error));
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "#f5f9e9",
        minHeight: viewHightMinusHeader,
        display: "flex",
        justifyContent: "center"
      }}
      className="body"
    >
      <Container sx={{
        paddingTop: 2,
        paddingBottom: 2
      }}>
        {/* Loading */}
        {messagesLoading && (
          <Box sx={{ textAlign: "center" }}>
            <LottieIndicator data={loadingAnimationData} message="Searching For Your Messages" />
          </Box>
        )}

        {/* Error */}
        {!messagesLoading && error && (
          <Box sx={{ textAlign: "center" }}>
            <LoginWarning />
            <Typography color="#162C1F">{error}</Typography>
          </Box>
        )}

        {/* Empty State */}
        {!messagesLoading && !error && messages.length === 0 && (
          <Box
            style={{
              width: "100%" // Subtracting drawer width
            }}
            sx={{
              textAlign: "center",
              minHeight: "100%",
              display: "flex",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <LottieIndicator
              data={nothingHereAnimationData}
              message="You Have No Notifications... Check back later"
            />
          </Box>
        )}

        {/* Messages */}
        {!messagesLoading && !error && messages.length > 0 && (
          <Grid container spacing={3}>
            {messages.map((msg) => (
              <Grid item xs={12} sm={8} md={6} lg={4} key={msg.id}>
                {msg.type === "staff_invitation" && (
                  <StaffInvitationMessage
                    messages={messages}
                    message={msg}
                    setError={setError}
                    setMessages={setMessages}
                    user={user}
                  />
                )}
                {msg.type === "refund_request" && (
                  <RefundMessage
                    messages={messages}
                    message={msg}
                    setError={setError}
                    setMessages={setMessages}
                    user={user}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default MessagesPage;
